import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty, debounce } from 'lodash'
import queryString from 'query-string'

import { Loader } from '@/components/loaders/Loader'
import { Menu } from '@/components/navigation/Menu'
import {
  COACHING_INBOX,
  COMPLETED,
  COMPLIANCE_INBOX,
  EXPIRED,
  IGNORED,
  INBOX,
  INBOX_DEBOUNCE_MS,
  INBOX_PAGE_LIMIT,
  QA_INBOX,
  READY_FOR_REVIEW,
  TASK_TYPE_MAP,
} from '@/reducers/tasks/tasks.constants'
import {
  fetchTasksPaginated,
  fetchTaskById,
  patchTask,
  bulkPatchTasks,
} from '@/reducers/tasks/tasks.actions'
import { setTask, setSavedFilterId, resetTaskList } from '@/reducers/tasks/tasks.redux'
import { AdvancedTablePagination } from '@/components/tables/AdvancedTable/AdvancedTablePagination'
import { INBOX_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { resetMediaPlayer } from '@/reducers/media/media.redux'

import { InboxFilters } from './components/InboxFilters'
import { InboxZero } from './components/InboxZero'
import { determineGradient } from '../helpers'
import { TaskCard } from './components/TaskCard'
import { BulkActions } from './components/BulkActions'
import {
  TODO,
  ARCHIVE,
  DEFAULT_SORT_CONFIG,
  MANUAL_COMPLIANCE_TYPE,
  getTestIdForTask,
  parseComplianceTypeFilter,
} from './inbox.helpers'
import { SavedFilters } from '../savedFilters/SavedFilters'
import { CoachingSelfService } from './components/CoachingSelfService'
import { Task } from './components/Task'
import { PageHeader } from '../headers/PageHeader'

import './Inbox.scss'

export const Inbox = ({ inboxType, title }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { loading, data, task, tasks, selectedSavedFilterId, pagination, counts, types } =
    useSelector((state) => state.tasks)
  const { savedFilterList } = useSelector((state) => state.savedFilters)
  const { user_id: currentUserId } = useSelector((state) => state.currentUser)
  const [taskStatus, setTaskStatus] = useState('')
  const [taskCreatedBy, setTaskCreatedBy] = useState('')
  const [taskAgentReviewed, setTaskAgentReviewed] = useState('')
  const [taskType, setTaskType] = useState('')
  const [selectedAgents, setSelectedAgents] = useState([])
  const [complianceType, setComplianceType] = useState('')
  const [complianceTypeOptions, setComplianceTypeOptions] = useState([])
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG)
  const [openTab, setOpenTab] = useState(TODO)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedTaskIds, setSelectedTaskIds] = useState([])
  const [allPagesSelected, setAllPagesSelected] = useState(false)
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const { search } = location
  const isArchive = openTab === ARCHIVE

  const getTaskStatuses = () => {
    if (isArchive) {
      if (taskStatus) {
        return [taskStatus]
      }
      return [COMPLETED, EXPIRED, IGNORED]
    }
    return [READY_FOR_REVIEW]
  }

  const getAgentReviewedFilter = () => {
    if (!isArchive || !taskAgentReviewed) {
      return null
    }
    return taskAgentReviewed === 'true'
  }

  const getCreatedByFilter = () => {
    if (taskCreatedBy === 'my') {
      return currentUserId
    }
    return null
  }

  const currentlyAppliedFilter = savedFilterList[INBOX_FILTER_TYPE]?.find(
    (filter) => filter.uuid === selectedSavedFilterId
  )
  const agentsWithColors = data.agents.map((agent, index) => ({
    ...agent,
    color: determineGradient(index),
  }))
  const activeSavedFilterAgents = currentlyAppliedFilter
    ? currentlyAppliedFilter.filters.agents.filter((agent) =>
        data.agents.some((activeAgent) => activeAgent.value === agent.value)
      )
    : []
  const suggestedAgentsWithColors = activeSavedFilterAgents.map((agent) => ({
    ...agent,
    color: agentsWithColors.find((coloredAgent) => coloredAgent.value === agent.value)?.color,
  }))
  // A saved filter that names agents already scopes the inbox to them, so the dropdown narrows
  // within it. One that only filters by tag leaves every agent selectable.
  const savedFilterScopesAgents = !isEmpty(currentlyAppliedFilter?.filters.agents)
  const agentFilterOptions = savedFilterScopesAgents ? activeSavedFilterAgents : data.agents

  const handleSelectSavedFilter = (value) => {
    if (!value) {
      dispatch(setSavedFilterId(null))
    } else {
      dispatch(setSavedFilterId(value))
    }
  }

  const getCurrentFilters = () => {
    const savedFilterAgentIds = currentlyAppliedFilter
      ? currentlyAppliedFilter.filters.agents.map((agent) => agent.value)
      : []
    const agentIds = isEmpty(selectedAgents)
      ? savedFilterAgentIds
      : selectedAgents.map((agent) => agent.value)
    const tagIds = currentlyAppliedFilter
      ? currentlyAppliedFilter.filters.tags.map((tag) => tag.value)
      : []

    const taskTypes = inboxType === QA_INBOX && taskType ? [taskType] : TASK_TYPE_MAP[inboxType]

    const { aiGenerated, taskName } =
      inboxType === COMPLIANCE_INBOX ? parseComplianceTypeFilter(complianceType) : {}

    return {
      taskTypes,
      agentIds,
      tagIds,
      taskStatuses: getTaskStatuses(),
      agentReviewed: getAgentReviewedFilter(),
      createdByUserId: getCreatedByFilter(),
      aiGenerated,
      taskName,
    }
  }

  const fetchTasksWithFilters = (page = 1) => {
    const filters = getCurrentFilters()

    dispatch(
      fetchTasksPaginated({
        ...filters,
        page,
        limit: INBOX_PAGE_LIMIT,
        sortBy: sortConfig.sortBy,
        sortOrder: sortConfig.orderBy,
      })
    )
  }

  // Ref to hold latest fetchTasksWithFilters for debounced calls
  const fetchRef = useRef(fetchTasksWithFilters)
  fetchRef.current = fetchTasksWithFilters

  const debouncedFetch = useRef(
    debounce((page) => {
      fetchRef.current(page)
    }, INBOX_DEBOUNCE_MS)
  ).current

  const handleUpdateTask = async (taskId, organizationId, status, handleClose) => {
    await dispatch(patchTask(taskId, { status }, inboxType, organizationId))

    if (handleClose) {
      handleClose()
    }

    fetchTasksWithFilters(currentPage)
  }

  const handleOpenTaskDrawer = (task) => {
    dispatch(setTask(task))

    const search = queryString.stringify({ id: task.id })

    history.push({ search })
  }

  const handleCloseTaskDrawer = () => {
    dispatch(setTask(null))
    dispatch(resetMediaPlayer())

    history.push({})
  }

  const handleSelectTask = (taskId) => {
    setAllPagesSelected(false)
    if (selectedTaskIds.includes(taskId)) {
      setSelectedTaskIds(selectedTaskIds.filter((id) => id !== taskId))
    } else {
      setSelectedTaskIds([...selectedTaskIds, taskId])
    }
  }

  const handleToggleSelectAll = () => {
    if (selectedTaskIds.length === tasks.length) {
      setSelectedTaskIds([])
      setAllPagesSelected(false)
    } else {
      setSelectedTaskIds(tasks.map((task) => task.id))
    }
  }

  const handleSelectAllPages = () => {
    setSelectedTaskIds(tasks.map((task) => task.id))
    setAllPagesSelected(true)
  }

  const handleDeselectAll = () => {
    setSelectedTaskIds([])
    setAllPagesSelected(false)
  }

  const handleBulkAction = async (targetStatus) => {
    if (isEmpty(selectedTaskIds) && !allPagesSelected) {
      return
    }

    setBulkUpdating(true)

    const organizationId = tasks.find((task) => selectedTaskIds.includes(task.id))?.organization_id

    if (allPagesSelected) {
      const {
        taskTypes,
        agentIds,
        tagIds,
        taskStatuses,
        agentReviewed,
        createdByUserId,
        aiGenerated,
        taskName,
      } = getCurrentFilters()
      await dispatch(
        bulkPatchTasks(null, { status: targetStatus }, organizationId, {
          task_types: taskTypes,
          agent_ids: !isEmpty(agentIds) ? agentIds : undefined,
          tag_ids: !isEmpty(tagIds) ? tagIds : undefined,
          task_statuses: taskStatuses,
          agent_reviewed: agentReviewed,
          created_by_user_id: createdByUserId,
          ai_generated: aiGenerated,
          task_name: taskName,
        })
      )
    } else {
      await dispatch(bulkPatchTasks(selectedTaskIds, { status: targetStatus }, organizationId))
    }

    setSelectedTaskIds([])
    setAllPagesSelected(false)
    setBulkUpdating(false)
    fetchTasksWithFilters(currentPage)
  }

  const allPageSelected = selectedTaskIds.length === tasks.length && !isEmpty(tasks)
  const hasMultiplePages = pagination.totalPages > 1

  const inboxes = [
    { id: TODO, title: 'Inbox', secondaryAction: 'Dismiss', primaryAction: 'View' },
    { id: ARCHIVE, title: 'Archive', secondaryAction: 'Reopen', primaryAction: 'View' },
  ]

  const currentTab = inboxes.find((inbox) => inbox.id === openTab)

  // Update compliance type options from backend-provided types. The response's
  // types are scoped to the current query, so skip while a type filter is
  // applied — otherwise a filtered fetch collapses the options to its own
  // result set and the selected type can no longer resolve to an option.
  // The backend types only cover AI-generated tasks; Manual is the bucket for
  // everything else, so it's always offered.
  useEffect(() => {
    if (inboxType === COMPLIANCE_INBOX && !complianceType) {
      const sourceTypes = Array.isArray(types) ? types : []
      setComplianceTypeOptions([
        { value: MANUAL_COMPLIANCE_TYPE, label: MANUAL_COMPLIANCE_TYPE },
        ...sourceTypes.map((label) => ({ value: label, label })),
      ])
    }
  }, [types, inboxType, complianceType])

  // AdvancedTablePagination passes either a page number or an updater function
  const handlePageChange = (pageOrUpdater) => {
    setSelectedTaskIds([])
    setAllPagesSelected(false)
    if (typeof pageOrUpdater === 'function') {
      setCurrentPage((prevPage) => {
        const newPage = pageOrUpdater(prevPage)
        fetchTasksWithFilters(newPage)
        return newPage
      })
    } else {
      setCurrentPage(pageOrUpdater)
      fetchTasksWithFilters(pageOrUpdater)
    }
  }

  // The task list is one store slice shared by every inbox, and nothing empties it when an
  // inbox unmounts. Without this, arriving at Quality from Coaching paints Coaching's rows
  // under the loading overlay until Quality's own (possibly empty) page replaces them.
  // A layout effect so the reset commits before the browser paints the first frame, and
  // before the (passive) fetching effects below run, whatever order they are declared in.
  useLayoutEffect(() => {
    dispatch(resetTaskList())
  }, [inboxType])

  useEffect(() => {
    // Fetch task on initial load if id is in query params (e.g. direct link navigation)
    const params = queryString.parse(search)
    // eslint-disable-next-line eqeqeq
    if (params.id && task?.id != params.id) {
      dispatch(fetchTaskById(params.id, handleOpenTaskDrawer, handleCloseTaskDrawer))
    }
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
    setSelectedTaskIds([])
    setAllPagesSelected(false)

    // A different saved filter offers a different set of agents, so the selection is stale.
    // Clearing it retriggers the debounced fetch below, so skip the immediate one.
    if (!isEmpty(selectedAgents)) {
      setSelectedAgents([])
      return
    }

    fetchTasksWithFilters(1)
  }, [currentlyAppliedFilter])

  // Refetch immediately when tab changes
  useEffect(() => {
    setCurrentPage(1)
    setSelectedTaskIds([])
    setAllPagesSelected(false)
    fetchTasksWithFilters(1)
  }, [openTab])

  const isInitialMount = useRef(true)

  // Refetch when filter/sort changes (debounced)
  useEffect(() => {
    // The other effects already fetch on mount, so the first run would only duplicate them
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    setCurrentPage(1)
    setSelectedTaskIds([])
    setAllPagesSelected(false)
    debouncedFetch(1)
  }, [
    sortConfig,
    taskStatus,
    taskAgentReviewed,
    taskCreatedBy,
    taskType,
    selectedAgents,
    complianceType,
  ])

  useEffect(() => {
    if (inboxType !== QA_INBOX) {
      setTaskType('')
    }

    setComplianceType('')
    setCurrentPage(1)
    setSelectedTaskIds([])
    setAllPagesSelected(false)

    // Not every inbox offers the agent filter, and the selection is per-inbox anyway
    if (!isEmpty(selectedAgents)) {
      setSelectedAgents([])
      return
    }

    fetchTasksWithFilters(1)
  }, [inboxType])

  return (
    <>
      <PageHeader
        decoration="lightning-bolt"
        data-testid="inbox-title"
        title={title}
        actions={
          <SavedFilters
            filterType={INBOX_FILTER_TYPE}
            noFiltersTitle="No Filters"
            noFiltersMessage="You have no filters"
            noFiltersButtonLabel="Create Filter"
            filterFormOptions={['name', 'agents', 'tags', 'isDefault']}
            selectedSavedFilterId={selectedSavedFilterId}
            handleSelectSavedFilter={handleSelectSavedFilter}
          />
        }
      />
      {inboxType === COACHING_INBOX && (
        <CoachingSelfService
          handleOpenTaskDrawer={handleOpenTaskDrawer}
          fetchTasksWithFilters={() => fetchTasksWithFilters(1)}
          suggestedAgents={suggestedAgentsWithColors}
        />
      )}
      <div>
        <Menu
          items={inboxes.map((tab) => ({
            accessor: tab.id,
            label: tab.title,
            badge: tab.id === TODO ? counts.inbox : undefined,
            active: openTab === tab.id,
            onClick: () => {
              setOpenTab(tab.id)
              setComplianceType('') // clear compliance type when switching tabs because the values could change
            },
            dataTestId: `tab-${tab.id}`,
          }))}
        />

        {loading[INBOX] && isEmpty(tasks) ? (
          <div className="empty">
            <Loader data-testid="loader" />
          </div>
        ) : (
          <div className="inbox-tasklist" data-testid="inbox-tasklist">
            {loading[INBOX] && (
              <div className="inbox-loading-overlay">
                <Loader data-testid="loader" />
              </div>
            )}
            <InboxFilters
              isArchive={isArchive}
              inboxType={inboxType}
              sortConfig={sortConfig}
              taskStatus={taskStatus}
              taskCreatedBy={taskCreatedBy}
              taskAgentReviewed={taskAgentReviewed}
              taskType={taskType}
              agentOptions={agentFilterOptions}
              agentsLoading={loading.agents}
              selectedAgents={selectedAgents}
              setSelectedAgents={setSelectedAgents}
              complianceType={complianceType}
              setTaskStatus={setTaskStatus}
              setTaskCreatedBy={setTaskCreatedBy}
              setTaskAgentReviewed={setTaskAgentReviewed}
              setSortConfig={setSortConfig}
              setTaskType={setTaskType}
              setComplianceType={setComplianceType}
              complianceTypeOptions={complianceTypeOptions}
            />

            {isEmpty(tasks) ? (
              <InboxZero
                isArchive={isArchive}
                inboxCount={counts.inbox}
                archiveCount={counts.archive}
                inboxType={inboxType}
                currentTab={currentTab}
              />
            ) : (
              <div className="inbox-tasks" data-testid={`inbox-tasks-${currentTab.id}`}>
                {!isEmpty(selectedTaskIds) && (
                  <BulkActions
                    selectedTaskIds={selectedTaskIds}
                    allPageSelected={allPageSelected}
                    allPagesSelected={allPagesSelected}
                    hasMultiplePages={hasMultiplePages}
                    totalCount={pagination.totalCount}
                    onSelectAll={handleToggleSelectAll}
                    onSelectAllPages={handleSelectAllPages}
                    onClear={handleDeselectAll}
                    onAction={() => handleBulkAction(isArchive ? READY_FOR_REVIEW : IGNORED)}
                    actionLabel={isArchive ? 'Reopen' : 'Dismiss'}
                    updating={bulkUpdating}
                  />
                )}
                {tasks.map((task, index) => {
                  const description = [COACHING_INBOX, COMPLIANCE_INBOX].includes(inboxType)
                    ? task.evidence?.[0]?.ai_explanation || task.description
                    : task.description
                  const agentWithColor = agentsWithColors.find(
                    (agent) => agent.value === task.agent_id
                  )
                  const createdBy = task?.created_by
                    ? `${task?.created_by_first_name} ${task?.created_by_last_name}`
                    : null
                  return (
                    <TaskCard
                      index={index}
                      key={task.id}
                      id={task.id}
                      dataTestId={getTestIdForTask(task)}
                      type={task.type}
                      title={task.name}
                      isAiGenerated={task.ai_generated}
                      agentReviewed={task.agent_reviewed}
                      createdBy={createdBy}
                      description={description}
                      callCount={task.available_evidence_count}
                      createdDate={task.created_at}
                      updatedDate={task.updated_at}
                      status={task.status}
                      agent={`${task.agent_first_name} ${task.agent_last_name}`}
                      color={agentWithColor?.color}
                      primaryActionButtonText={currentTab.primaryAction}
                      handlePrimaryAction={() => handleOpenTaskDrawer(task)}
                      secondaryActionButtonText={currentTab.secondaryAction}
                      handleSecondaryAction={
                        currentTab.id === ARCHIVE
                          ? () => handleUpdateTask(task.id, task.organization_id, READY_FOR_REVIEW)
                          : () => handleUpdateTask(task.id, task.organization_id, IGNORED)
                      }
                      callType={task.evidence?.[0]?.call_type}
                      selectable
                      selected={selectedTaskIds.includes(task.id)}
                      onSelect={() => handleSelectTask(task.id)}
                    />
                  )
                })}
                <AdvancedTablePagination
                  activePage={currentPage}
                  rowsPerPage={pagination.limit}
                  count={pagination.totalCount}
                  totalPages={pagination.totalPages}
                  setActivePage={handlePageChange}
                  transparent
                  label="task"
                />
              </div>
            )}
          </div>
        )}
      </div>
      <Task
        inboxType={inboxType}
        handleUpdateTask={handleUpdateTask}
        fetchTasksWithFilters={() => fetchTasksWithFilters(currentPage)}
      />
    </>
  )
}
